import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, FileText, CheckCircle, Trash2, Download, Eye, Pencil, Calendar, User, Filter, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayrollPage() {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const { t } = useLanguage();
  const { isHR, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Fetch employees for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get(`${API}/employees`);
        setEmployees(response.data.filter(e => e.is_active));
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  const fetchPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await axios.get(`${API}/payroll`, { params });
      setPayrolls(response.data);
    } catch (error) {
      console.error('Error fetching payrolls:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchPayrolls();
  }, [fetchPayrolls]);

  // Filter payrolls based on selected filters
  const filteredPayrolls = useMemo(() => {
    return payrolls.filter(p => {
      // Month filter
      if (monthFilter && !p.period_start?.startsWith(monthFilter)) {
        return false;
      }
      // Employee filter
      if (employeeFilter !== 'all' && p.employee_id !== employeeFilter) {
        return false;
      }
      return true;
    });
  }, [payrolls, monthFilter, employeeFilter]);

  // Get unique months from payrolls for quick selection
  const availableMonths = useMemo(() => {
    const months = new Set();
    payrolls.forEach(p => {
      if (p.period_start) {
        const month = p.period_start.substring(0, 7); // YYYY-MM
        months.add(month);
      }
    });
    return Array.from(months).sort().reverse();
  }, [payrolls]);

  const clearFilters = () => {
    setStatusFilter('all');
    setMonthFilter('');
    setEmployeeFilter('all');
  };

  const hasActiveFilters = statusFilter !== 'all' || monthFilter !== '' || employeeFilter !== 'all';

  const handleFinalize = async (id) => {
    if (!window.confirm(t('confirmFinalize'))) return;
    
    try {
      await axios.put(`${API}/payroll/${id}/finalize`);
      toast.success(t('savedSuccessfully'));
      fetchPayrolls();
    } catch (error) {
      console.error('Error finalizing payroll:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    
    try {
      await axios.delete(`${API}/payroll/${id}`);
      toast.success(t('deletedSuccessfully'));
      fetchPayrolls();
    } catch (error) {
      console.error('Error deleting payroll:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = t('errorOccurred');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const handleDownloadPdf = async (id, employeeName, periodStart) => {
    try {
      const response = await axios.get(`${API}/export/pdf/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lohnabrechnung_${employeeName.replace(' ', '_')}_${periodStart}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  if (loading && payrolls.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('payroll')}</h1>
        {isHR() && (
          <Button
            onClick={() => navigate('/payroll/new')}
            className="bg-[#E31E24] hover:bg-[#C41218] text-[#1A1A1A] btn-primary-glow"
            data-testid="add-payroll-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('newPayroll')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Month Filter */}
            <div className="flex-1">
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {t('monthYear')}
              </Label>
              <div className="flex gap-2">
                <Input
                  type="month"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="bg-white border-gray-200 text-[#1A1A1A]"
                  data-testid="month-filter"
                />
                {availableMonths.length > 0 && (
                  <Select value={monthFilter || 'select'} onValueChange={(v) => setMonthFilter(v === 'select' ? '' : v)}>
                    <SelectTrigger className="w-32 bg-white border-gray-200 text-[#1A1A1A]">
                      <SelectValue placeholder={t('quickSelect')} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="select">{t('allMonths')}</SelectItem>
                      {availableMonths.map(month => (
                        <SelectItem key={month} value={month}>
                          {formatMonthDisplay(month)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Employee Filter */}
            <div className="flex-1">
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <User className="w-3 h-3" />
                {t('employee')}
              </Label>
              <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                <SelectTrigger 
                  className="w-full bg-white border-gray-200 text-[#1A1A1A]"
                  data-testid="employee-filter"
                >
                  <SelectValue placeholder={t('allEmployees')} />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 max-h-60">
                  <SelectItem value="all">{t('allEmployees')}</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.employee_number && `[${emp.employee_number}] `}{emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="flex-1">
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <Filter className="w-3 h-3" />
                {t('status')}
              </Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger 
                  className="w-full bg-white border-gray-200 text-[#1A1A1A]"
                  data-testid="status-filter"
                >
                  <SelectValue placeholder={t('allStatuses')} />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">{t('allStatuses')}</SelectItem>
                  <SelectItem value="draft">{t('draft')}</SelectItem>
                  <SelectItem value="finalized">{t('finalized')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="text-gray-500 hover:text-[#E31E24] border-gray-200"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('clearFilters')}
                </Button>
              </div>
            )}
          </div>

          {/* Filter Summary */}
          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-gray-500">
              <span>{t('filterResults')}:</span>
              <Badge variant="secondary" className="bg-gray-100">
                {filteredPayrolls.length} {t('entries')}
              </Badge>
              {monthFilter && (
                <Badge variant="outline" className="border-blue-200 text-blue-600">
                  {formatMonthDisplay(monthFilter)}
                </Badge>
              )}
              {employeeFilter !== 'all' && (
                <Badge variant="outline" className="border-green-200 text-green-600">
                  {employees.find(e => e.id === employeeFilter)?.first_name} {employees.find(e => e.id === employeeFilter)?.last_name}
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="outline" className="border-amber-200 text-amber-600">
                  {statusFilter === 'draft' ? t('draft') : t('finalized')}
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="table-header">{t('employees')}</TableHead>
                  <TableHead className="table-header">{t('payrollPeriod')}</TableHead>
                  <TableHead className="table-header">{t('payoutGross')}</TableHead>
                  <TableHead className="table-header">{t('payoutNet')}</TableHead>
                  <TableHead className="table-header">{t('status')}</TableHead>
                  <TableHead className="table-header text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayrolls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-400">
                      {hasActiveFilters ? t('noDataForFilter') : t('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayrolls.map((payroll) => (
                    <TableRow
                      key={payroll.id}
                      className="border-gray-200 hover:bg-gray-50 cursor-pointer"
                      data-testid={`payroll-row-${payroll.id}`}
                    >
                      <TableCell className="font-medium text-[#1A1A1A]">
                        {payroll.employee_name}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {payroll.period_start} - {payroll.period_end}
                      </TableCell>
                      <TableCell className="font-mono text-[#1A1A1A]">
                        {formatCurrency(payroll.total_gross)}
                      </TableCell>
                      <TableCell className="font-mono">
                        {payroll.net_salary ? (
                          <span className="text-green-600 font-semibold">{formatCurrency(payroll.net_salary)}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {payroll.status === 'finalized' ? (
                          <Badge className="badge-success">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {t('finalized')}
                          </Badge>
                        ) : (
                          <Badge className="badge-warning">
                            <FileText className="w-3 h-3 mr-1" />
                            {t('draft')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/payroll/${payroll.id}`)}
                            className="text-gray-500 hover:text-blue-600"
                            title={t('viewDetails')}
                            data-testid={`view-${payroll.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {payroll.status === 'finalized' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPdf(payroll.id, payroll.employee_name, payroll.period_start)}
                              className="text-gray-500 hover:text-green-600"
                              title={t('downloadPdf')}
                              data-testid={`download-${payroll.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {payroll.status === 'draft' && isHR() && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/payroll/${payroll.id}/edit`)}
                                className="text-gray-500 hover:text-amber-600"
                                title={t('edit')}
                                data-testid={`edit-${payroll.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFinalize(payroll.id)}
                                className="text-gray-500 hover:text-green-600"
                                title={t('finalize')}
                                data-testid={`finalize-${payroll.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          
                          {isAdmin() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(payroll.id)}
                              className="text-gray-500 hover:text-red-600"
                              title={t('delete')}
                              data-testid={`delete-${payroll.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
